import { useState, useEffect, useCallback, useRef } from 'react';
import { Check, Loader2, AlertCircle, Copy, Server, Cloud, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { checkServerPlanAvailability, createServer, type CreateServerPayload } from '@/api/servers';
import type { ServerData } from '@/types/server';

interface AddServerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serverData: ServerData;
  sshPublicKey: string;
  billingPlan: string;
  projectId: string;
}

type Provider = 'doploy' | 'CustomSSH' | null;
type CpuType = 'shared' | 'dedicated' | null;
type AuthType = 'key' | 'password';

interface PlanAvailability {
  [plan: string]: {
    checking: boolean;
    available: boolean;
    message?: string;
  };
}

const REGIONS = [
  { id: 'nbg1', name: 'Nuremberg', country: 'Germany' },
  { id: 'fsn1', name: 'Falkenstein', country: 'Germany' },
  { id: 'hel1', name: 'Helsinki', country: 'Finland' },
  { id: 'sin', name: 'Singapore', country: 'Singapore' },
  { id: 'hil', name: 'Hillsboro', country: 'Oregon, USA' },
  { id: 'ash', name: 'Ashburn', country: 'Virginia, USA' },
];

export function AddServerDialog({
  open,
  onOpenChange,
  serverData,
  sshPublicKey,
  billingPlan,
  projectId,
}: AddServerDialogProps) {
  // State
  const [provider, setProvider] = useState<Provider>(null);
  const [cpuType, setCpuType] = useState<CpuType>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [planAvailability, setPlanAvailability] = useState<PlanAvailability>({});
  const availabilityRequestId = useRef(0);
  
  // Custom SSH state
  const [hostname, setHostname] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('');
  const [authType, setAuthType] = useState<AuthType>('key');
  const [password, setPassword] = useState('');
  const [hostnameError, setHostnameError] = useState('');
  const [copied, setCopied] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setProvider(null);
      setCpuType(null);
      setSelectedRegion(null);
      setSelectedPlan(null);
      setPlanAvailability({});
      setHostname('');
      setPort('22');
      setUsername('');
      setAuthType('key');
      setPassword('');
      setHostnameError('');
      setIsSubmitting(false);
    }
  }, [open]);

  // Check availability when region changes
  const checkAvailability = useCallback(async (region: string, plans: string[]) => {
    // Set all plans to checking
    const initialState: PlanAvailability = {};
    plans.forEach(plan => {
      initialState[plan] = { checking: true, available: false };
    });
    setPlanAvailability(initialState);

    // Track request to avoid stale updates
    const currentRequestId = ++availabilityRequestId.current;

    try {
      const results = await Promise.allSettled(
        plans.map((plan) =>
          checkServerPlanAvailability(plan, region).then((res) => ({ plan, res }))
        )
      );

      // Ignore if a newer request has started
      if (currentRequestId !== availabilityRequestId.current) return;

      const nextState: PlanAvailability = {};
      results.forEach((outcome, index) => {
        if (outcome.status === 'fulfilled') {
          const { plan, res } = outcome.value;
          nextState[plan] = {
            checking: false,
            available: res.available,
            message: res.error || res.message,
          };
        } else {
          const plan = plans[index];
          nextState[plan] = {
            checking: false,
            available: true, // Default to available on error
          };
        }
      });

      setPlanAvailability(nextState);
    } catch {
      // On unexpected failure, fall back to marking all available
      const fallback: PlanAvailability = {};
      plans.forEach((plan) => {
        fallback[plan] = { checking: false, available: true };
      });
      setPlanAvailability(fallback);
    }
  }, []);

  // When region changes, check availability
  useEffect(() => {
    if (selectedRegion && cpuType && serverData) {
      const plans = cpuType === 'shared' 
        ? Object.keys(serverData.shared_plans)
        : Object.keys(serverData.dedicated_plans);
      checkAvailability(selectedRegion, plans);
      setSelectedPlan(null); // Reset plan selection
    }
  }, [selectedRegion, cpuType, serverData, checkAvailability]);

  // Get current plans based on CPU type
  const currentPlans = cpuType === 'shared' 
    ? serverData?.shared_plans 
    : cpuType === 'dedicated' 
      ? serverData?.dedicated_plans 
      : {};

  // Get price for a plan
  const getPrice = (planId: string) => {
    if (!selectedRegion || !serverData?.pricing) return null;
    return serverData.pricing[selectedRegion]?.[planId];
  };

  // Get plan specs (may vary by region)
  const getPlanSpecs = (plan: typeof currentPlans[string]) => {
    if (selectedRegion && plan.region_specs?.[selectedRegion]) {
      return plan.region_specs[selectedRegion].description;
    }
    if (plan.vcpus && plan.ram && plan.disk) {
      return `${plan.vcpus} vCPU, ${plan.ram}GB RAM, ${plan.disk}GB SSD`;
    }
    return plan.description;
  };

  // Validation
  const ipRegex = /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/;
  const fqdnRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
  
  const validateHostname = (value: string) => {
    if (!value) return false;
    return ipRegex.test(value) || fqdnRegex.test(value);
  };

  const isDoployValid = provider === 'doploy' && cpuType && selectedRegion && selectedPlan;
  const isCustomSSHValid = provider === 'CustomSSH' && hostname && port && username && 
    (authType === 'key' || (authType === 'password' && password)) && validateHostname(hostname);
  const isFormValid = isDoployValid || isCustomSSHValid;

  // Handle form submission - submit to the Flask endpoint
  const handleSubmit = async () => {
    if (!isFormValid) return;
    
    setIsSubmitting(true);

    if (provider === 'CustomSSH' && !validateHostname(hostname)) {
      setHostnameError('Please enter a valid IP address or FQDN.');
      setIsSubmitting(false);
      return;
    }

    // Call the API endpoint to create the server
    try {
      const body: CreateServerPayload = {
        selected_provider: provider!,
      };
      
      if (provider === 'doploy') {
        body.cpu_type = cpuType!;
        body.region = selectedRegion!;
        body.server_plan = selectedPlan!;
      } else if (provider === 'CustomSSH') {
        body.hostname = hostname;
        body.port = port;
        body.username = username;
        body.auth_type = authType;
        if (authType === 'password') {
          body.password = password;
        }
      }

      const payload = await createServer(projectId, body);

      if (payload.redirect_url) {
        // Redirect to Stripe checkout
        window.location.href = payload.redirect_url;
      } else {
        // For CustomSSH servers, close dialog and refresh page
        onOpenChange(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to create server:', error);
      alert(error instanceof Error ? error.message : 'Failed to create server');
      setIsSubmitting(false);
    }
  };

  const copySSHKey = () => {
    navigator.clipboard.writeText(sshPublicKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isStarterPlan = billingPlan === 'starter_plan';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Server</DialogTitle>
          <DialogDescription className="sr-only">
            Choose provider, CPU type, region, and plan to create a server.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Provider Selection */}
          <div>
            <h3 className="text-sm font-medium mb-3">Choose Provider</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* doploy Cloud */}
              <button
                type="button"
                onClick={() => {
                  setProvider('doploy');
                  setCpuType(null);
                  setSelectedRegion(null);
                  setSelectedPlan(null);
                }}
                className={cn(
                  "p-6 border-2 rounded-lg text-center transition-all hover:border-primary hover:shadow-md",
                  provider === 'doploy' ? "border-primary bg-primary/5" : "border-muted"
                )}
              >
                <Cloud className="h-12 w-12 mx-auto mb-3 text-primary" />
                <h4 className="font-semibold">doploy Cloud</h4>
                <p className="text-sm text-muted-foreground">Managed cloud servers with automatic billing</p>
              </button>

              {/* Bring Your Own Server */}
              {isStarterPlan ? (
                <div className="p-6 border-2 border-muted rounded-lg text-center bg-muted/30">
                  <Server className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <h4 className="font-semibold text-muted-foreground">Bring Your Own Server</h4>
                  <p className="text-sm text-muted-foreground mb-3">Connect your own server via SSH</p>
                  <Alert className="text-left">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      <strong>Not available on Starter plan.</strong> Upgrade to Pro plan to use your own servers.
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setProvider('CustomSSH');
                    setCpuType(null);
                    setSelectedRegion(null);
                    setSelectedPlan(null);
                  }}
                  className={cn(
                    "p-6 border-2 rounded-lg text-center transition-all hover:border-primary hover:shadow-md",
                    provider === 'CustomSSH' ? "border-primary bg-primary/5" : "border-muted"
                  )}
                >
                  <Server className="h-12 w-12 mx-auto mb-3 text-primary" />
                  <h4 className="font-semibold">Bring Your Own Server</h4>
                  <p className="text-sm text-muted-foreground">Connect your own server via SSH</p>
                </button>
              )}
            </div>
          </div>

          {/* doploy Configuration */}
          {provider === 'doploy' && (
            <>
              {/* CPU Type Selection */}
              <div>
                <h3 className="text-sm font-medium mb-3">Choose CPU Type</h3>
                <Alert className="mb-3">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <strong>Not sure which to choose?</strong> Start with Shared CPU for cost-effectiveness. You can always upgrade later.
                  </AlertDescription>
                </Alert>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCpuType('shared');
                      setSelectedRegion(null);
                      setSelectedPlan(null);
                    }}
                    className={cn(
                      "p-4 border-2 rounded-lg text-left transition-all hover:border-primary",
                      cpuType === 'shared' ? "border-primary bg-primary/5" : "border-muted"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5",
                        cpuType === 'shared' ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {cpuType === 'shared' && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div>
                        <h4 className="font-medium">Shared CPU</h4>
                        <p className="text-xs text-muted-foreground">Best for staging and light production workloads.</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCpuType('dedicated');
                      setSelectedRegion(null);
                      setSelectedPlan(null);
                    }}
                    className={cn(
                      "p-4 border-2 rounded-lg text-left transition-all hover:border-primary",
                      cpuType === 'dedicated' ? "border-primary bg-primary/5" : "border-muted"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5",
                        cpuType === 'dedicated' ? "border-primary bg-primary" : "border-muted"
                      )}>
                        {cpuType === 'dedicated' && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div>
                        <h4 className="font-medium">Dedicated CPU</h4>
                        <p className="text-xs text-muted-foreground">Best for production and high CPU usage.</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Region Selection */}
              {cpuType && (
                <div>
                  <h3 className="text-sm font-medium mb-3">Choose Region</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {REGIONS.map(region => (
                      <button
                        key={region.id}
                        type="button"
                        onClick={() => setSelectedRegion(region.id)}
                        className={cn(
                          "p-3 border-2 rounded-lg text-center transition-all hover:border-primary",
                          selectedRegion === region.id ? "border-primary bg-primary/5" : "border-muted"
                        )}
                      >
                        <div className="text-2xl mb-1">{region.name}</div>
                        <div className="text-xs text-muted-foreground">{region.country}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Plan Selection */}
              {selectedRegion && (
                <div>
                  <h3 className="text-sm font-medium mb-3">Choose Server Plan</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(currentPlans || {}).map(([planId, plan]) => {
                      const price = getPrice(planId);
                      const availability = planAvailability[planId];
                      const isAvailable = !availability?.checking && availability?.available !== false;
                      const isChecking = availability?.checking;

                      return (
                        <button
                          key={planId}
                          type="button"
                          onClick={() => isAvailable && setSelectedPlan(planId)}
                          disabled={!isAvailable || isChecking}
                          className={cn(
                            "p-4 border-2 rounded-lg text-left transition-all relative",
                            selectedPlan === planId ? "border-primary bg-primary/5" : "border-muted",
                            !isAvailable && "opacity-50 cursor-not-allowed",
                            isAvailable && "hover:border-primary"
                          )}
                        >
                          {selectedPlan === planId && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{getPlanSpecs(plan)}</p>
                              <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                            </div>
                            <div className="text-right ml-4">
                              <span className="font-bold text-green-600">
                                {price !== null ? `$${price.toFixed(2)}/mo` : '-'}
                              </span>
                            </div>
                          </div>
                          {/* Availability indicator */}
                          <div className="mt-2 pt-2 border-t">
                            {isChecking ? (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Checking availability...
                              </span>
                            ) : isAvailable ? (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                Available
                              </span>
                            ) : (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Unavailable
                                {availability?.message && <span className="text-muted-foreground ml-1">- {availability.message}</span>}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {cpuType === 'dedicated' && (
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        <strong>Need even more power?</strong> Contact our support team for custom dedicated server configurations.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Billing Summary */}
              {selectedPlan && (
                <div className="bg-green-50 dark:bg-green-950/20 border-2 border-green-500 rounded-lg p-4">
                  <h3 className="font-medium mb-2">Billing Summary</h3>
                  <div className="flex justify-between items-center">
                    <span>{currentPlans?.[selectedPlan]?.description}</span>
                    <strong className="text-green-600">${getPrice(selectedPlan)?.toFixed(2)}/month</strong>
                  </div>
                  <Alert className="mt-3 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-xs text-yellow-800 dark:text-yellow-200">
                      <strong>Important:</strong> Servers are billed monthly and are <strong>not prorated</strong>. Billing starts immediately.
                    </AlertDescription>
                  </Alert>
                  <Alert className="mt-2">
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      You can always scale your server to a larger plan by contacting support.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </>
          )}

          {/* Custom SSH Configuration */}
          {provider === 'CustomSSH' && (
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Supported OS: <strong>Ubuntu 24.04 LTS</strong> or <strong>Ubuntu 22.04 LTS</strong><br />
                  Server should be a clean installation of Ubuntu.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="hostname">Hostname</Label>
                  <Input
                    id="hostname"
                    value={hostname}
                    onChange={(e) => {
                      setHostname(e.target.value);
                      setHostnameError('');
                    }}
                    placeholder="IP address or domain name"
                    className={hostnameError ? 'border-red-500' : ''}
                  />
                  {hostnameError && <p className="text-sm text-red-500 mt-1">{hostnameError}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    This can be an IP address or a fully qualified domain name (FQDN).
                  </p>
                </div>

                <div>
                  <Label htmlFor="port">Port</Label>
                  <Input
                    id="port"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="22"
                  />
                </div>

                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="root"
                  />
                </div>

                <div>
                  <Label>Authentication Type</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="auth_type"
                        value="key"
                        checked={authType === 'key'}
                        onChange={() => setAuthType('key')}
                        className="w-4 h-4"
                      />
                      SSH Key (Recommended)
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="auth_type"
                        value="password"
                        checked={authType === 'password'}
                        onChange={() => setAuthType('password')}
                        className="w-4 h-4"
                      />
                      Password
                    </label>
                  </div>
                </div>

                {authType === 'password' && (
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                )}

                {authType === 'key' && (
                  <div>
                    <Label>SSH Public Key</Label>
                    <div className="relative">
                      <textarea
                        readOnly
                        value={sshPublicKey || 'No SSH Key Available'}
                        className="w-full h-24 p-3 pr-20 border rounded-md bg-muted text-sm font-mono resize-none"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={copySSHKey}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Copy this key and add it to your server's <code>~/.ssh/authorized_keys</code>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Server'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
